import { SortDirection } from '@mui/material/TableCell';
import { useInfiniteQuery, useQuery, UseQueryResult } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import { Req } from '@cbo/shared-library';
import useInventoryRequests, { VendorItemsListOptions } from '.';
import staleTimeSettings from '../../config/reactQueryConfig';
import { useSites } from '../../contexts/siteContext';
import { Paginated } from '../../models/Paginated';
import useCurrentSite from '../../utils/hooks/useCurrentSite';
import { CategoryData } from '../models/Category';
import { InvoiceData, InvoiceStatus } from '../models/Invoice';
import { CostCalcMethodApiResponse, RawItemData, UnitData, ValidateRawItemCustomUnitData } from '../models/RawItem';
import {
  CombinedUnit,
  ContainerUnit,
  CustomUnit,
  IndustryUnit,
  ReceivingUnit,
  VendorInventoryUnitsMap,
} from '../models/UnitsOfMeasure';
import { VendorData } from '../models/Vendor';
import { VendorItemData } from '../models/VendorItem';
import useQueryKeys from './queryKeys';
import {
  CogsRequest,
  FilterValueRange,
  GetAllModifierGroupsResponse,
  GetOneModifierResponse,
  InvoiceHistoryGLAccountDetailReportCommonRequest,
  InvoiceHistoryGLCardsRequest,
  InvoiceHistoryGLGridRequest,
  InvoiceHistoryVendorDetailReportCommonRequest,
  InvoiceHistoryVendorSummaryReportCommonRequest,
  Top20ItemUsageReportRequest,
  PerpetualInventoryRequest,
} from '../types';

import { GetRecipeDto, RecipeDto, RecipeRow, RecipesFilterOptionsResponse, RecipesFiltersType } from '../models/Recipe';

import {
  InventorySummaryResponse,
  ListItem,
  ListsSummaryData,
  LocationDetailResponse,
  LocationSummary,
  LocationSummaryResponse,
  MobileInventoryStatus,
} from '../models/Location';
import {
  PurchaseOrderAcknowledgementDetails,
  PurchaseOrderDetails,
  PurchaseOrderItem,
  PurchaseOrdersGridRowResponse,
  PurchaseOrderStatus,
} from '../models/PurchaseOrders';
import { RecipeItemRow } from '../models/RecipeItem';
import {
  GetAllSalesItemsMappingsResponse,
  GetAllSalesItemsResponse,
  GetOneSalesItemsResponse,
  GetUnmappedSalesItemsResponse,
} from '../models/SalesItem';
import SalesUtilities from '../../../sales/utilities';
import { BaseReportDataRequest } from '../../../api/sales/requests';
import { transformRecipeFilters } from '../utilities';
import { organizationViewSite } from '../../admin/SiteSelector/SiteSelector';
import { ItemsOnHandQueries, RawItemsOnHandResponse } from '../models/RealTimeInventory';

/**
 * INVOICES
 */

export const useInvoicesPaginatedDataQuery = (
  page: number,
  limit: number,
  filters?: string[],
  sort?: string
): UseQueryResult<Paginated<InvoiceData>> => {
  const { getInvoicesPaginatedData } = useInventoryRequests();

  return useQuery(
    useQueryKeys().invoices.listWithPage(page, limit, filters, sort),
    () => getInvoicesPaginatedData(page, limit, filters, sort),
    {
      keepPreviousData: true,
    }
  );
};

export const useInvoicesByVendorDataQuery = (
  vendorId: string,
  status?: InvoiceStatus
): UseQueryResult<Paginated<InvoiceData>> => {
  const { getInvoicesByVendorData } = useInventoryRequests();

  return useQuery(
    useQueryKeys().invoices.listByVendorId(vendorId, status),
    () => getInvoicesByVendorData(vendorId, status),
    {
      enabled: !!vendorId,
      staleTime: staleTimeSettings.NONE,
    }
  );
};

export const useInvoiceDataQuery = (invoiceId: string) => {
  const { getInvoiceData } = useInventoryRequests();

  return useQuery(useQueryKeys().invoices.detailsById(invoiceId), () => getInvoiceData(invoiceId), {
    enabled: !!invoiceId,
    staleTime: staleTimeSettings.NONE,
  });
};
/**
 * INVOICE ITEMS
 */

export const useInvoiceItemsDataQuery = (invoiceId: string, addReceivingUnits: boolean, addLocations: boolean) => {
  const { getInvoiceItemsData } = useInventoryRequests();

  return useQuery(
    useQueryKeys().invoiceItems.listById(invoiceId),
    () => getInvoiceItemsData(invoiceId, addReceivingUnits, addLocations),
    {
      enabled: !!invoiceId,
      staleTime: staleTimeSettings.NONE,
    }
  );
};

/**
 * VENDORS
 */

export const useVendorsPaginatedDataQuery = (
  page: number,
  limit: number,
  filters?: string[],
  sort?: string,
  active?: boolean,
  draft?: boolean
): UseQueryResult<Paginated<VendorData>> => {
  const { getVendorsPaginatedData } = useInventoryRequests();

  return useQuery(
    useQueryKeys().vendors.listWithPage(page, limit, filters, sort),
    () => getVendorsPaginatedData(page, limit, filters, sort, active, draft),
    {
      keepPreviousData: true,
    }
  );
};

export const useVendorsByNameDataQuery = (
  query?: string,
  limit?: number,
  active?: boolean
): UseQueryResult<VendorData[]> => {
  const { findVendorsByName } = useInventoryRequests();

  return useQuery(
    useQueryKeys().vendors.listWithQuery(query),
    // This is a workaround because trying to call findVendorsByName endpoint with no keyword is throwing 500 error
    () => findVendorsByName(query, active, limit, false),
    {
      staleTime: staleTimeSettings.LOW,
    }
  );
};

export const useVendorDataQuery = (vendorId: string): UseQueryResult<VendorData> => {
  const { getVendorData } = useInventoryRequests();

  return useQuery(useQueryKeys().vendors.detailsById(vendorId), () => getVendorData(vendorId), {
    staleTime: staleTimeSettings.LOW,
    enabled: !!vendorId,
    refetchOnWindowFocus: false,
  });
};

/**
 * VENDOR ITEMS
 */

export const useVendorItemsDataQuery = (
  vendorId: string,
  options?: VendorItemsListOptions,
  refetchOnWindowFocus = false
): UseQueryResult<Paginated<VendorItemData>> => {
  const { getVendorItemsData } = useInventoryRequests();

  // keepPreviousData keeps stale data visible while new params (page, keyword, etc.) are fetching,
  // which prevents rowCount from briefly dropping to 0 and causing MUI DataGrid pagination resets.
  // We re-derive isLoading as isLoading||isFetching so all callers correctly reflect an active fetch
  // whether it is the initial load or a background param-change refetch.
  const result = useQuery(
    useQueryKeys().vendorItems.listById(vendorId, options),
    () => getVendorItemsData(vendorId, options),
    {
      enabled: !!vendorId,
      staleTime: staleTimeSettings.NONE,
      refetchOnWindowFocus,
      keepPreviousData: true,
    }
  );

  const isLoadingOrFetching = result.isLoading || result.isFetching;
  return { ...result, isLoading: isLoadingOrFetching } as UseQueryResult<Paginated<VendorItemData>>;
};

export const useUnmappedVendorItemsQuery = (
  vendorId: string,
  options?: VendorItemsListOptions
): UseQueryResult<Paginated<VendorItemData>> => {
  const { getVendorItemsData } = useInventoryRequests();

  return useQuery(
    useQueryKeys().vendorItems.listUnmappedItemsById(vendorId, options),
    () => getVendorItemsData(vendorId, options),
    {
      enabled: !!vendorId,
      staleTime: staleTimeSettings.NONE,
      refetchOnWindowFocus: false,
    }
  );
};

const useVendorItemsDataSubQuery = (
  vendorId: string,
  select: (data: Paginated<VendorItemData>) => string[],
  options?: VendorItemsListOptions
): UseQueryResult<string[]> => {
  const { getVendorItemsData } = useInventoryRequests();

  return useQuery(useQueryKeys().vendorItems.listById(vendorId, options), () => getVendorItemsData(vendorId, options), {
    select,
    enabled: !!vendorId,
    staleTime: staleTimeSettings.LOW,
  });
};

export const useVendorItemNamesQuery = (
  vendorId: string,
  options: VendorItemsListOptions
): UseQueryResult<string[] | undefined> =>
  useVendorItemsDataSubQuery(vendorId, (data) => data.items.map((item) => item.name), options);

export const useVendorItemCodesQuery = (vendorId: string, options: VendorItemsListOptions): UseQueryResult<string[]> =>
  useVendorItemsDataSubQuery(vendorId, (data) => data.items.map((item) => item.itemCode), options);

/**
 * SERVICE ITEMS
 */

export const useServiceItemsDataQuery = (
  select?: (data: CategoryData[]) => CategoryData[]
): UseQueryResult<CategoryData[]> => {
  const { getServiceItemsData } = useInventoryRequests();

  return useQuery(useQueryKeys().serviceItems.list(), getServiceItemsData, {
    select,
    staleTime: staleTimeSettings.HIGH,
  });
};

export const useFilteredServiceItemsDataQuery = (filter: string): UseQueryResult<CategoryData[]> => {
  const { getServiceItemsData } = useInventoryRequests();
  const select = (data: CategoryData[]) =>
    data.filter(
      (item) =>
        item &&
        (item.accountName.toLowerCase().includes(filter.toLowerCase()) ||
          String(item.accountNumber?.toString()).includes(filter))
    );

  return useQuery(useQueryKeys().serviceItems.listWithFilter(filter), getServiceItemsData, {
    select,
    staleTime: staleTimeSettings.HIGH,
  });
};

const useServiceItemsDataSubQuery = (select: (data: CategoryData[]) => string[]): UseQueryResult<string[]> => {
  const { getServiceItemsData } = useInventoryRequests();

  return useQuery(useQueryKeys().serviceItems.list(), getServiceItemsData, {
    select,
    staleTime: staleTimeSettings.HIGH,
  });
};

export const useServiceItemNamesQuery = (): UseQueryResult<string[]> =>
  useServiceItemsDataSubQuery((data) => data.map((item) => item.accountName));

export const useServiceItemCodesQuery = (): UseQueryResult<string[]> =>
  useServiceItemsDataSubQuery((data) => data.map((item) => item.categoryId.toString()));

/**
 * UNITS OF MEASURE
 */
export const useReceivingUnitsQuery = (receivingUnitId: string): UseQueryResult<ReceivingUnit> => {
  const { getReceivingUnitData } = useInventoryRequests();

  return useQuery(
    useQueryKeys().units.receiving.detailsById(receivingUnitId),
    () => getReceivingUnitData(receivingUnitId),
    {
      enabled: !!receivingUnitId,
      staleTime: staleTimeSettings.NONE,
    }
  );
};

export const useAllReceivingUnitsQuery = (): UseQueryResult<ReceivingUnit[]> => {
  const { getAllReceivingUnitDataBySite } = useInventoryRequests();

  return useQuery(useQueryKeys().units.receiving.all(), getAllReceivingUnitDataBySite, {
    staleTime: staleTimeSettings.HIGH,
  });
};

export const useContainerUnitsQuery = (): UseQueryResult<ContainerUnit[]> => {
  const { getContainerData } = useInventoryRequests();

  return useQuery(useQueryKeys().units.container(), getContainerData, {
    staleTime: staleTimeSettings.HIGH,
  });
};

export const useStartingUnitsQuery = (): UseQueryResult<ContainerUnit[]> => {
  const { getStartingUnitsData } = useInventoryRequests();

  return useQuery(useQueryKeys().units.containerWithStarting(), getStartingUnitsData, {
    staleTime: staleTimeSettings.HIGH,
  });
};

export const useIndustryUnitsQuery = (): UseQueryResult<IndustryUnit[]> => {
  const { getUnitsData } = useInventoryRequests();

  return useQuery(useQueryKeys().units.industryWithReceiving(), getUnitsData, {
    staleTime: staleTimeSettings.HIGH,
  });
};

export const useAllIndustryUnitsQuery = (): UseQueryResult<IndustryUnit[]> => {
  const { getAllUnitsData } = useInventoryRequests();

  return useQuery(useQueryKeys().units.industry(), getAllUnitsData, {
    staleTime: staleTimeSettings.HIGH,
  });
};

export const useCustomUnitsQuery = (): UseQueryResult<CustomUnit[]> => {
  const { getCustomUnitsData } = useInventoryRequests();

  return useQuery(useQueryKeys().units.custom.all(), getCustomUnitsData, {
    staleTime: staleTimeSettings.HIGH,
  });
};

export const useCombinedUnitsQuery = (
  page: number,
  limit: number,
  filters: string[],
  sortBy = 'updatedAt',
  sortOrder = 'DESC'
): UseQueryResult<Paginated<CombinedUnit>> => {
  const { getCombinedUnitsData } = useInventoryRequests();

  return useQuery(
    useQueryKeys().units.combined.listWithPage(page, limit, filters, sortBy, sortOrder),
    () =>
      getCombinedUnitsData({
        page,
        limit,
        filters,
        sortBy,
        sortOrder,
      }),
    {
      keepPreviousData: true,
      staleTime: staleTimeSettings.LOW,
    }
  );
};

export const useIsInUseQuery = (customUnitId: string): UseQueryResult<boolean> => {
  const { getIsInUseData } = useInventoryRequests();

  return useQuery(useQueryKeys().units.custom.isInUse(customUnitId), () => getIsInUseData(customUnitId), {
    staleTime: staleTimeSettings.HIGH,
  });
};

export const useGetCustomUnitQuery = (customUnitId: string): UseQueryResult<CustomUnit> => {
  const { getCustomUnit } = useInventoryRequests();

  return useQuery(useQueryKeys().units.custom.isInUse(customUnitId), () => getCustomUnit(customUnitId), {
    staleTime: staleTimeSettings.HIGH,
  });
};

/**
 * RECIPE ITEMS
 */
export const useRecipePaginatedDataQuery = (
  page: number,
  limit: number,
  filters?: RecipesFiltersType,
  gridFilters?: string[],
  sort?: string,
  active?: boolean,
  draft?: boolean
): UseQueryResult<Paginated<RecipeRow>> => {
  const { getRecipesPaginatedData } = useInventoryRequests();

  const params: {
    page: number;
    limit: number;
    filters?: string[];
    gridFilters?: string[];
    sort?: string;
    active?: boolean;
    draft?: boolean;
  } = {
    page,
    limit,
    filters: [],
    gridFilters: [],
    sort,
    active,
    draft,
  };

  const transformedFilters = filters ? transformRecipeFilters(filters) : [];
  params.filters = transformedFilters.length > 0 ? transformedFilters : undefined;
  params.gridFilters = gridFilters && gridFilters.length > 0 ? gridFilters : undefined;
  return useQuery(
    useQueryKeys().recipes.listWithPage(page, limit, params.filters, params.gridFilters, sort),
    () => getRecipesPaginatedData(params),
    {
      keepPreviousData: true,
      staleTime: staleTimeSettings.HIGH,
    }
  );
};

export const useRecipeFiltersOptionsQuery = (
  page: number,
  limit: number
): UseQueryResult<RecipesFilterOptionsResponse> => {
  const { getRecipesFilterOptionsData } = useInventoryRequests();
  const params = {
    page,
    limit,
  };

  return useQuery(useQueryKeys().recipes.filterOptions(page, limit), () => getRecipesFilterOptionsData(params), {
    keepPreviousData: true,
    staleTime: staleTimeSettings.HIGH,
  });
};

export const useRecipeFiltersByFieldOptionsQuery = (
  page: number,
  limit: number,
  field: string,
  value: string | undefined
): UseQueryResult<Partial<RecipesFilterOptionsResponse>> => {
  const { getRecipesFilterOptionsByFieldData } = useInventoryRequests();
  const params = {
    page,
    limit,
    field,
    value,
  };

  return useQuery(
    useQueryKeys().recipes.filterOptionsByField(page, limit, field, value),
    () => getRecipesFilterOptionsByFieldData(params),
    {
      keepPreviousData: true,
      staleTime: staleTimeSettings.MEDIUM,
      enabled: !!field && !!value, // only run when field, value is provided
    }
  );
};

export const useRecipeItemsPaginatedDataQuery = (
  recipeData: RecipeDto,
  page: number,
  limit: number,
  filters?: string[],
  sort?: string
): UseQueryResult<Paginated<RecipeItemRow>> => {
  const { getRecipeItemsPaginatedData } = useInventoryRequests();
  const params = {
    page,
    limit,
    filters,
    sort,
  };

  return useQuery(
    useQueryKeys().recipeItems.listWithPage(recipeData.id as string, page, limit, filters, sort),
    () => getRecipeItemsPaginatedData(recipeData.id as string, params),
    {
      keepPreviousData: true,
      staleTime: staleTimeSettings.HIGH,
    }
  );
};

export const useRecipeIdsDataQuery = (): UseQueryResult<string[]> => {
  const { getRecipes } = useInventoryRequests();

  return useQuery(useQueryKeys().recipes.list(), () => getRecipes(true), {
    staleTime: staleTimeSettings.MEDIUM,
  });
};

export const useRecipeDataQuery = (recipeId: string): UseQueryResult<GetRecipeDto> => {
  const { getRecipeData } = useInventoryRequests();

  return useQuery(useQueryKeys().recipes.detailsById(recipeId), () => getRecipeData(recipeId), {
    staleTime: staleTimeSettings.MEDIUM,
    enabled: !isEmpty(recipeId),
  });
};

export const useGetUnassignedPrepAndRawItemsQuery = (
  id: string,
  filterPrepItem?: boolean,
  find?: string,
  enabled?: boolean
): UseQueryResult<GetAllSalesItemsMappingsResponse> => {
  const { getUnassignedPrepAndRawItems } = useInventoryRequests();

  return useQuery(
    useQueryKeys().recipes.unassignedItems(id, filterPrepItem, find),
    () => getUnassignedPrepAndRawItems(id, filterPrepItem, find),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled,
    }
  );
};

/**
 * RAW ITEMS
 */

export const useRawItemsDataQuery = (
  page: number,
  limit: number,
  find?: string,
  filters?: string[],
  active?: boolean,
  sort?: string
): UseQueryResult<Paginated<RawItemData>> => {
  const { getRawItems } = useInventoryRequests();
  return useQuery(
    useQueryKeys().rawItems.listWithPage(page, limit, find, filters, active, sort),
    () =>
      getRawItems({
        page,
        limit,
        find,
        filters,
        active,
        sort,
      }),
    { keepPreviousData: true, staleTime: staleTimeSettings.HIGH }
  );
};

export const useRawItemDataQuery = (rawItemId: string): UseQueryResult<RawItemData> => {
  const { getRawItem } = useInventoryRequests();

  return useQuery(useQueryKeys().rawItems.detailsById(rawItemId), () => getRawItem(rawItemId), {
    enabled: !!rawItemId,
    staleTime: staleTimeSettings.MEDIUM,
  });
};

export const useGetUsagePerLastCalcDate = () => {
  const { siteId } = useCurrentSite();
  const { getUsagePerLastCalcDate } = useInventoryRequests();

  return useQuery(useQueryKeys().rawItems.getLastCalculated(siteId), () => getUsagePerLastCalcDate(siteId), {
    enabled: !!siteId && siteId !== organizationViewSite.id,
    useErrorBoundary: false,
  });
};

export const useValidateRawItemCustomUnitQuery = (rawItemId: string): UseQueryResult<ValidateRawItemCustomUnitData> => {
  const { validateRawItemCustomUnits } = useInventoryRequests();
  return useQuery(
    useQueryKeys().rawItems.customUnitValidation(rawItemId),
    () => validateRawItemCustomUnits(rawItemId),
    {
      enabled: !!rawItemId,
    }
  );
};

export const useCostCalcMethodOptionsQuery = (): UseQueryResult<CostCalcMethodApiResponse> => {
  const { getCostCalcMethodOptions } = useInventoryRequests();
  const queryKeys = useQueryKeys();
  return useQuery({
    queryKey: queryKeys.rawItems.costCalcMethodOptions,
    queryFn: getCostCalcMethodOptions,
    staleTime: staleTimeSettings.HIGH,
  });
};

export const useRawItemRecipeUnitQuery = (rawItemId: string): UseQueryResult<UnitData> => {
  const { getRecipeUnit } = useInventoryRequests();

  return useQuery(useQueryKeys().rawItems.recipeUnitById(rawItemId), () => getRecipeUnit(rawItemId), {
    enabled: !!rawItemId,
  });
};

export const useGetUsagePerQuery = (rawItemId: string, isOrgLevelView: boolean): UseQueryResult<number> => {
  const { getUsagePer } = useInventoryRequests();
  const { siteId: currentSiteId } = useCurrentSite();
  return useQuery(useQueryKeys().rawItems.usagePer(rawItemId), () => getUsagePer(rawItemId, currentSiteId), {
    enabled: !!rawItemId && !!currentSiteId && !isOrgLevelView,
    useErrorBoundary: false,
  });
};

export const useDefaultUsagePerFactorQuery = (): UseQueryResult<number> => {
  const { getDefaultUsagePerFactor } = useInventoryRequests();
  return useQuery(useQueryKeys().rawItems.defaultUsagePerFactor, () => getDefaultUsagePerFactor());
};

export const useRawItemReportingUnitQuery = (rawItemId: string): UseQueryResult<UnitData> => {
  const { getReportingUnit } = useInventoryRequests();
  const { currentSite } = useCurrentSite();
  const { lastSelectedSite } = useSites();
  const euid = currentSite.id === 'organizationView' ? lastSelectedSite.enterpriseUnitId : currentSite.enterpriseUnitId;
  return useQuery(useQueryKeys().rawItems.reportingUnitById(rawItemId), () => getReportingUnit(rawItemId, euid), {
    enabled: !!rawItemId && euid.length > 0,
  });
};

export const useGetAllMappedVendorItemsQuery = (
  rawItemId: string | undefined,
  enabled?: boolean
): UseQueryResult<Paginated<VendorItemData>> => {
  const { getRawItemMappedVendorItems } = useInventoryRequests();

  return useQuery(
    useQueryKeys().vendorItems.getMappedVendorItems(rawItemId),
    () =>
      getRawItemMappedVendorItems(rawItemId, {
        page: 1,
        limit: 100,
        addVendor: true,
        addReceivingUnits: true,
        onlyUnmapped: false,
      }),
    { staleTime: staleTimeSettings.MEDIUM, enabled }
  );
};

export const useRawItemMappedVendorItemsQuery = (
  rawItemId: string | undefined,
  page: number,
  limit: number,
  addReceivingUnits?: boolean,
  addVendor?: boolean,
  onlyUnmapped?: boolean,
  addRawItems?: boolean,
  filters?: string[],
  sort?: string
): UseQueryResult<Paginated<VendorItemData>> => {
  const { getRawItemMappedVendorItems } = useInventoryRequests();

  return useQuery(
    useQueryKeys().vendorItems.listWithRawItemAndPage(
      rawItemId,
      page,
      limit,
      addReceivingUnits,
      addVendor,
      addRawItems,
      onlyUnmapped,
      filters,
      sort
    ),
    () =>
      getRawItemMappedVendorItems(rawItemId, {
        page,
        limit,
        addVendor,
        addReceivingUnits,
        onlyUnmapped,
        addRawItems,
        filters,
        sort,
      }),
    { staleTime: staleTimeSettings.MEDIUM }
  );
};

export const useRawItemMappedVendorItemUnitClassesQuery = (rawItemId: string): UseQueryResult<string[]> => {
  const { getRawItemMappedVendorItemUnitClasses } = useInventoryRequests();
  return useQuery(
    useQueryKeys().rawItems.getRawItemUnitClasses(rawItemId),
    () => getRawItemMappedVendorItemUnitClasses(rawItemId),

    { staleTime: staleTimeSettings.MEDIUM, enabled: rawItemId.length > 0 }
  );
};

export const useRawItemMappedVendorItemUnitsQuery = (
  id: string,
  page: number,
  limit: number,
  find?: string,
  filter?: string,
  sort?: string,
  enabled?: boolean
): UseQueryResult<VendorInventoryUnitsMap> => {
  const { getRawItemMappedVendorItemUnits } = useInventoryRequests();

  return useQuery(
    useQueryKeys().rawItems.listWithUnitsPage(id, page, limit, find, filter, sort),
    () =>
      getRawItemMappedVendorItemUnits({
        id,
        page,
        limit,
        find,
        filter,
        sort,
      }),
    { keepPreviousData: true, enabled }
  );
};

export const useRawItemAllVendorItemsQuery = (
  rawItemId: string | undefined,
  page: number,
  limit: number,
  find?: string,
  addReceivingUnits?: boolean,
  addVendor?: boolean
): UseQueryResult<Paginated<VendorItemData>> => {
  const { getRawItemMappedVendorItems } = useInventoryRequests();

  return useQuery(
    useQueryKeys().vendorItems.listWithRawItemAndPage(rawItemId, page, limit, addReceivingUnits, addVendor),
    () =>
      getRawItemMappedVendorItems(rawItemId, {
        page,
        limit,
        addVendor,
        addReceivingUnits,
      }),
    { keepPreviousData: true }
  );
};

/**
 * INVOICE HISTORY REPORTS
 */

export const useGetVendorSummaryReportInvoiceCountCardData = () => {
  const { getVendorSummaryReportInvoiceCountCardData } = useInventoryRequests();
  const [request, setGetVendorSummaryReportInvoiceCountCardRequest] = useState({
    startDate: '',
    endDate: '',
  } as InvoiceHistoryVendorSummaryReportCommonRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useQueryKeys().invHistVendorSummary.kpiCards.invoiceCount(
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.previousStartDate,
      request.previousEndDate,
      request.vendors?.sort()
    ),
    () => getVendorSummaryReportInvoiceCountCardData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: false,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    vendorSummaryReportInvoiceCountCardData: data,
    setGetVendorSummaryReportInvoiceCountCardRequest,
    isGetVendorSummaryReportInvoiceCountCardLoading: isLoading,
  };
};

export const useGetVendorSummaryReportInvoiceAmountCardData = () => {
  const { getVendorSummaryReportInvoiceAmountCardData } = useInventoryRequests();
  const [request, setGetVendorSummaryReportInvoiceAmountCardRequest] = useState({
    startDate: '',
    endDate: '',
  } as InvoiceHistoryVendorSummaryReportCommonRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useQueryKeys().invHistVendorSummary.kpiCards.invoiceAmount(
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.previousStartDate,
      request.previousEndDate,
      request.vendors?.sort()
    ),
    () => getVendorSummaryReportInvoiceAmountCardData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: false,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    vendorSummaryReportInvoiceAmountCardData: data,
    setGetVendorSummaryReportInvoiceAmountCardRequest,
    isGetVendorSummaryReportInvoiceAmountCardLoading: isLoading,
  };
};

export const useGetInvoiceTotalAmountByVendorData = () => {
  const { getInvoiceTotalAmountByVendorData } = useInventoryRequests();
  const [request, setGetInvoiceTotalAmountByVendorRequest] = useState({
    startDate: '',
    endDate: '',
  } as InvoiceHistoryVendorSummaryReportCommonRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useQueryKeys().invHistVendorSummary.kpiCards.totalByVendor(
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.vendors?.sort()
    ),
    () => getInvoiceTotalAmountByVendorData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: false,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    invoiceTotalAmountByVendorData: data,
    setGetInvoiceTotalAmountByVendorRequest,
    isGetInvoiceTotalAmountByVendorLoading: isLoading,
  };
};

export const useGetVendorSummaryGridReportData = () => {
  const { getVendorSummaryReportGridData } = useInventoryRequests();
  const [request, setGetVendorSummaryReportGridDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as InvoiceHistoryVendorSummaryReportCommonRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useQueryKeys().invHistVendorSummary.gridData(
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.vendors?.sort()
    ),
    () => getVendorSummaryReportGridData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: false,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    vendorSummaryReportGridData: data,
    setGetVendorSummaryReportGridDataRequest,
    isGetVendorSummaryReportGridDataLoading: isLoading,
  };
};

export const useGetVendorDetailReportCardsData = () => {
  const { getVendorDetailReportCardsData } = useInventoryRequests();
  const [request, setGetVendorDetailReportCardsRequest] = useState({
    startDate: '',
    endDate: '',
  } as InvoiceHistoryVendorDetailReportCommonRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useQueryKeys().invHistVendorDetails.kpiCards(
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.vendor
    ),
    () => getVendorDetailReportCardsData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: false,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    vendorDetailReportCardsData: data,
    setGetVendorDetailReportCardsRequest,
    isGetVendorDetailReportCardsDataLoading: isLoading,
  };
};

export const useGetVendorDetailReportGridData = () => {
  const { getVendorDetailReportGridData } = useInventoryRequests();
  const [request, setGetVendorDetailReportGridDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as InvoiceHistoryVendorDetailReportCommonRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useQueryKeys().invHistVendorDetails.gridData(
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.vendor
    ),
    () => getVendorDetailReportGridData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: false,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    vendorDetailReportGridData: data,
    setGetVendorDetailReportGridDataRequest,
    isGetVendorDetailReportGridDataLoading: isLoading,
  };
};

export const useGetGLAccountReportGridData = () => {
  const { getGLAccountReportGridData } = useInventoryRequests();
  const [request, setGetGLAccountReportGridDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as InvoiceHistoryGLGridRequest);

  /* istanbul ignore next */
  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    [
      'invoice-history',
      'by-category',
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.glSubcategories,
      request.glAccounts,
    ],
    () => getGLAccountReportGridData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: false,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    GLAccountReportGridData: data,
    setGetGLAccountReportGridDataRequest,
    isGetGLAccountReportGridDataLoading: isLoading,
  };
};

export const useGetGLAccountReportKPINumbersData = () => {
  const { getGLAccountReportKPINumbersData } = useInventoryRequests();
  const [request, setGLAccountReportKPINumbersDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as InvoiceHistoryGLCardsRequest);

  /* istanbul ignore next */
  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    [
      'invoice-history',
      'by-category',
      'kpi-numbers',
      request.startDate,
      request.endDate,
      request.previousStartDate,
      request.previousEndDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.glSubcategories,
      request.glAccounts,
    ],
    () => getGLAccountReportKPINumbersData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: false,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    GLAccountReportKPINumbersData: data,
    setGLAccountReportKPINumbersDataRequest,
    isGetGLAccountReportKPINumbersDataLoading: isLoading,
  };
};

export const useGetGLAccountReportKPIChartData = () => {
  const { getGLAccountReportKPIChartData } = useInventoryRequests();
  const [request, setGLAccountReportKPIChartDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as InvoiceHistoryGLGridRequest);

  /* istanbul ignore next */
  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    [
      'invoice-history',
      'by-category',
      'kpi-chart',
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.glSubcategories,
      request.glAccounts,
    ],
    () => getGLAccountReportKPIChartData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: false,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    GLAccountReportKPIChartData: data,
    setGLAccountReportKPIChartDataRequest,
    isGetGLAccountReportKPIChartDataLoading: isLoading,
  };
};

export const useGetGLAccountDetailReportGridData = () => {
  const { getGLAccountDetailReportGridData } = useInventoryRequests();
  const [request, setGetGLAccountDetailReportGridDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as InvoiceHistoryGLAccountDetailReportCommonRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useQueryKeys().invHistGLAccountDetails.gridData(
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.glSubcategories,
      request.glAccounts,
      request.vendorIds
    ),
    () => getGLAccountDetailReportGridData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: false,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    glAccountDetailReportGridData: data,
    setGetGLAccountDetailReportGridDataRequest,
    isGetGLAccountDetailReportGridDataLoading: isLoading,
  };
};

export const useGetGLAccountDetailReportCardsData = () => {
  const { getGLAccountDetailReportCardsData } = useInventoryRequests();
  const [request, setGetGLAccountDetailReportCardsDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as InvoiceHistoryGLAccountDetailReportCommonRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useQueryKeys().invHistGLAccountDetails.kpiCards(
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.glSubcategories,
      request.glAccounts,
      request.vendorIds
    ),
    () => getGLAccountDetailReportCardsData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: false,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    glAccountDetailReportCardsData: data,
    setGetGLAccountDetailReportCardsDataRequest,
    isGetGLAccountDetailReportCardsDataLoading: isLoading,
  };
};

/**
 * LOCATIONS
 */

export interface UseGetInventoryListsDataQueryOptions {
  filters?: string[];
  sortBy?: string;
  sortOrder?: SortDirection;
  includePrepItems?: boolean;
}

export const useGetInventoryListsDataQuery = (
  page: number,
  limit: number,
  options?: UseGetInventoryListsDataQueryOptions
): UseQueryResult<Paginated<ListsSummaryData>> => {
  const { filters, sortBy, sortOrder, includePrepItems } = options || {};
  const { getInventoryLists } = useInventoryRequests();

  return useQuery(
    useQueryKeys().inventoryLists.listWithPage(page, limit, filters, sortBy, sortOrder, includePrepItems),
    () => getInventoryLists({ page, limit, filters, sortBy, sortOrder, includePrepItems }),
    {
      staleTime: staleTimeSettings.HIGH,
      keepPreviousData: true,
    }
  );
};

export const useGetItemsByInventoryList = (
  listId: string,
  showAssigned: boolean,
  page: number,
  limit: number,
  filter?: string,
  sort?: string,
  includePrepItems?: boolean
): UseQueryResult<Paginated<ListItem>> => {
  const { getItemsByInventoryList } = useInventoryRequests();

  return useQuery(
    useQueryKeys().inventoryLists.listById(listId, showAssigned, page, limit, filter, sort, includePrepItems),
    () => getItemsByInventoryList(listId, showAssigned, { page, limit, filter, sort, includePrepItems }),
    {
      keepPreviousData: true,
    }
  );
};

export const useGetAllSelectedItemsByInventoryList = (
  listId: string,
  isUnassigned: boolean,
  includePrepItems?: boolean
) => {
  const { getItemsByInventoryList } = useInventoryRequests();

  const { data, refetch, isLoading, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery(
    useQueryKeys().rawItems.listAllByLocation(listId, includePrepItems),
    ({ pageParam }) =>
      getItemsByInventoryList(listId, true, {
        page: pageParam?.pagenum ?? 1,
        limit: 100, // Arbitrary number for batching
        includePrepItems,
      }),
    {
      enabled: !isUnassigned,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage: Paginated<ListItem>) =>
        lastPage.meta.currentPage >= lastPage.meta.totalPages ? undefined : { pagenum: lastPage.meta.currentPage + 1 },
    }
  );

  return {
    data,
    isLoading,
    isFetching,
    refetch,
    hasNextPage,
    fetchNextPage,
  };
};

export const useGetLocationsDataQuery = (includePrepItems?: boolean): UseQueryResult<LocationSummary> => {
  const { getInventoryLocations } = useInventoryRequests();
  const { currentSiteId } = useCurrentSite();
  const prevSiteIdRef = useRef<string | undefined>(undefined);
  const initialSiteIdRef = useRef<string | undefined>(undefined);

  const result = useQuery(
    useQueryKeys().locations.list(includePrepItems),
    () => getInventoryLocations(includePrepItems),
    {
      staleTime: staleTimeSettings.MEDIUM,
      refetchOnWindowFocus: false,
      useErrorBoundary: false,
    }
  );

  useEffect(() => {
    // Store the initial site ID on first load
    if (initialSiteIdRef.current === undefined && currentSiteId) {
      initialSiteIdRef.current = currentSiteId;
    }

    // Refetch only when switching back to the initial/default site from another site
    if (
      prevSiteIdRef.current !== undefined &&
      prevSiteIdRef.current !== currentSiteId &&
      currentSiteId === initialSiteIdRef.current &&
      currentSiteId
    ) {
      result.refetch();
    }

    prevSiteIdRef.current = currentSiteId;
  }, [currentSiteId, result]);

  return result;
};

export const useGetRawItemsByLocation = (
  locationId: string,
  page: number,
  limit: number,
  getSelectedOnly?: boolean,
  filters?: string[],
  sort?: string,
  sortOrder?: SortDirection,
  includePrepItems?: boolean
): UseQueryResult<LocationDetailResponse> => {
  const { getRawItemsByLocation } = useInventoryRequests();

  return useQuery(
    useQueryKeys().rawItems.listByLocation(
      locationId,
      page,
      limit,
      getSelectedOnly,
      filters,
      sort,
      sortOrder,
      includePrepItems
    ),
    () =>
      getRawItemsByLocation(locationId, {
        page,
        limit,
        getSelectedOnly,
        filters,
        sortBy: sort,
        sortOrder,
        includePrepItems,
      }),
    { keepPreviousData: true }
  );
};

export const useGetAllSequencedItemsByLocation = (locationId: string, includePrepItems?: boolean) => {
  const { getRawItemsByLocation } = useInventoryRequests();

  const { data, refetch, isLoading, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery(
    useQueryKeys().rawItems.listAllByLocation(locationId, includePrepItems),
    ({ pageParam }) =>
      getRawItemsByLocation(locationId, {
        page: pageParam?.pagenum ?? 1,
        limit: 100, // Arbitrary number for batching
        getSelectedOnly: true,
        sortBy: 'sequence',
        sortOrder: 'asc',
        includePrepItems,
      }),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage: LocationDetailResponse) =>
        lastPage.meta.currentPage >= lastPage.meta.totalPages ? undefined : { pagenum: lastPage.meta.currentPage + 1 },
    }
  );

  return {
    data,
    isLoading,
    isFetching,
    refetch,
    hasNextPage,
    fetchNextPage,
  };
};

/**
 * INVENTORY COUNTING
 */

export const useGetInventorySummaryByLocation = (
  summaryId: string,
  locationId: string,
  searchValue?: string,
  includePrepItems?: boolean,
  enabled = true,
  limit = 100
) => {
  const { getInventorySummaryByLocation } = useInventoryRequests();
  const queryKeys = useQueryKeys();

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery(
    queryKeys.inventorySummary.byLocation(summaryId, locationId, searchValue, includePrepItems),
    ({ pageParam }) =>
      getInventorySummaryByLocation(summaryId, locationId, {
        page: pageParam?.pagenum ?? 1,
        limit,
        searchValue,
        includePrepItems,
      }),
    {
      enabled,
      keepPreviousData: false,
      staleTime: staleTimeSettings.HIGH,
      getNextPageParam: (lastPage: LocationSummaryResponse) =>
        lastPage.inventoryLocation.meta.currentPage >= lastPage.inventoryLocation.meta.totalPages
          ? undefined
          : { pagenum: lastPage.inventoryLocation.meta.currentPage + 1 },
    }
  );

  return {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
  };
};

export const useGetInventorySummary = (
  filters: {
    isMaxValue?: boolean;
    statuses?: MobileInventoryStatus[];
    dateRange?: { startDate?: string | null; endDate?: string | null };
    skippedPercentage?: FilterValueRange;
    inventoryValue?: FilterValueRange;
  },
  includePrepItems?: boolean
) => {
  const { getInventorySummary } = useInventoryRequests();

  const validateDate = (date: string | null | undefined) =>
    date && dayjs(date).isValid() ? dayjs(date).toISOString() : undefined;

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery(
    useQueryKeys().inventorySummary.list(
      filters.statuses,
      filters.inventoryValue?.min,
      filters.isMaxValue ? 0 : filters.inventoryValue?.max,
      filters.dateRange?.startDate,
      filters.dateRange?.endDate,
      filters.skippedPercentage?.min,
      filters.skippedPercentage?.max,
      includePrepItems
    ),
    ({ pageParam }) =>
      getInventorySummary({
        page: pageParam?.pagenum ?? 1,
        limit: 100,
        statuses: filters.statuses,
        minValue: filters.inventoryValue?.min,
        maxValue: filters.inventoryValue?.max,
        startDate: validateDate(filters.dateRange?.startDate),
        endDate: validateDate(filters.dateRange?.endDate),
        minSkippedPercentage: filters.skippedPercentage?.min,
        maxSkippedPercentage: filters.skippedPercentage?.max,
        sortBy: 'status',
        includePrepItems,
      }),
    {
      keepPreviousData: false,
      staleTime: staleTimeSettings.HIGH,
      getNextPageParam: (lastPage: InventorySummaryResponse) =>
        lastPage.meta.currentPage >= lastPage.meta.totalPages ? undefined : { pagenum: lastPage.meta.currentPage + 1 },
    }
  );
  return {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
  };
};

export const useGetInventorySummaryList = (id: string, includePrepItems?: boolean) => {
  const { getInventorySummaryList } = useInventoryRequests();

  return useQuery(
    useQueryKeys().inventorySummary.listById(id, includePrepItems),
    () => getInventorySummaryList(id, includePrepItems),
    {
      staleTime: staleTimeSettings.HIGH,
      keepPreviousData: true,
      useErrorBoundary: false,
    }
  );
};

/**
 * MENU ITEMS
 */

export const useGetAllSalesItemsQuery = (): UseQueryResult<GetAllSalesItemsResponse> => {
  const { getAllSalesItems } = useInventoryRequests();

  return useQuery(useQueryKeys().salesItems.gridData(), () => getAllSalesItems(), {
    staleTime: staleTimeSettings.HIGH,
  });
};

export const useGetOneSalesItemsQuery = (itemCode: string): UseQueryResult<GetOneSalesItemsResponse> => {
  const { getOneSalesItem } = useInventoryRequests();

  return useQuery(useQueryKeys().salesItems.byItemCode(itemCode), () => getOneSalesItem(itemCode), {
    keepPreviousData: true,
    staleTime: staleTimeSettings.HIGH,
    enabled: !!itemCode,
  });
};

export const useUnmappedSalesItemsQuery = (
  keyword?: string,
  limit?: number,
  page?: number,
  enabled = true
): UseQueryResult<GetUnmappedSalesItemsResponse> => {
  const { getUnmappedSalesItems } = useInventoryRequests();

  return useQuery(
    useQueryKeys().salesItems.unmappedSuggestions(keyword, limit, page),
    () => getUnmappedSalesItems(keyword, limit, page),
    {
      staleTime: staleTimeSettings.MEDIUM,
      keepPreviousData: true,
      enabled,
    }
  );
};

export const useGetAllRecipesAndRawItemsQuery = (
  filterPrepItem?: boolean,
  find?: string
): UseQueryResult<GetAllSalesItemsMappingsResponse> => {
  const { getAllRecipesAndRawItems } = useInventoryRequests();

  return useQuery(
    useQueryKeys().salesItems.mappings(filterPrepItem, find),
    () => getAllRecipesAndRawItems(filterPrepItem, find),
    {
      staleTime: staleTimeSettings.HIGH,
      keepPreviousData: true,
    }
  );
};

export const useGetAllModifierGroupsQuery = (): UseQueryResult<GetAllModifierGroupsResponse> => {
  const { getAllModifierGroups } = useInventoryRequests();

  return useQuery(useQueryKeys().modifierGroups.gridData(), () => getAllModifierGroups(), {
    staleTime: staleTimeSettings.HIGH,
  });
};

export const useGetOneModifierGroupsQuery = (itemCode: string): UseQueryResult<GetOneModifierResponse> => {
  const { getOneModifierGroup } = useInventoryRequests();

  return useQuery(useQueryKeys().modifierGroups.byItemCode(itemCode), () => getOneModifierGroup(itemCode), {
    keepPreviousData: true,
    staleTime: staleTimeSettings.HIGH,
    enabled: !!itemCode,
  });
};

export const useGetCogsFiltersQuery = (enabled: boolean) => {
  const { getCogsFilters } = useInventoryRequests();
  const [filterRequest, setFilterRequest] = useState<BaseReportDataRequest>({
    startDate: '',
    endDate: '',
    siteIds: [],
  });

  const { startDate, endDate, siteIds } = filterRequest;

  const isEnabled =
    startDate.length > 0 &&
    endDate.length > 0 &&
    startDate !== 'Invalid Date' &&
    endDate !== 'Invalid Date' &&
    siteIds.length > 0 &&
    enabled;

  const { data, isLoading } = useQuery(
    useQueryKeys().cogs.filters(
      startDate,
      endDate,
      siteIds.sort((a, b) => a.localeCompare(b))
    ),
    () => getCogsFilters({ startDate, endDate, siteIds }),
    {
      staleTime: staleTimeSettings.HIGH,
      enabled: isEnabled,
    }
  );

  return {
    data,
    isLoading,
    setFilterRequest,
  };
};

export const useGetCogsAvailableDates = (siteId: string) => {
  const { getCogsAvailableDates } = useInventoryRequests();

  const isEnabled = siteId.length > 0 && siteId !== 'organizationView';

  const { data, isLoading } = useQuery(
    useQueryKeys().cogs.availableDates(siteId),
    () => getCogsAvailableDates(siteId),
    {
      staleTime: staleTimeSettings.HIGH,
      enabled: isEnabled,
    }
  );

  return { data, isLoading };
};

export const useGetCogsKPICardsQuery = (enabled: boolean) => {
  const { getCogsKPICards } = useInventoryRequests();

  const [request, setGetCogsKPICardsRequest] = useState({
    startDate: '',
    endDate: '',
    siteIds: [],
  } as CogsRequest);

  const { startDate, endDate, siteIds, glAccounts, reportingUnits, itemPrice, daysOnHand } = request;

  const isEnabled =
    enabled &&
    startDate.length > 0 &&
    endDate.length > 0 &&
    startDate !== 'Invalid Date' &&
    endDate !== 'Invalid Date' &&
    siteIds.length > 0;

  const { data, isLoading } = useQuery(
    useQueryKeys().cogs.kpiCards(
      request.startDate,
      request.endDate,
      request.siteIds.sort((a, b) => a.localeCompare(b)),
      glAccounts,
      reportingUnits,
      itemPrice,
      daysOnHand
    ),
    () => getCogsKPICards(request),
    {
      staleTime: staleTimeSettings.HIGH,
      enabled: isEnabled,
    }
  );

  return {
    cogsKPICardDataResponse: data,
    setGetCogsKPICardsRequest,
    kpiCardsLoading: isLoading,
    request,
  };
};

export const useGetCogsDataGridQuery = (enabled: boolean) => {
  const { getCogsDataGrid } = useInventoryRequests();

  const [gridRequest, setGridRequest] = useState({
    startDate: '',
    endDate: '',
    siteIds: [],
  } as CogsRequest);

  const { startDate, endDate, siteIds } = gridRequest;

  const isEnabled =
    enabled &&
    startDate.length > 0 &&
    endDate.length > 0 &&
    startDate !== 'Invalid Date' &&
    endDate !== 'Invalid Date' &&
    siteIds.length > 0;

  const { data, isLoading } = useQuery(
    useQueryKeys().cogs.dataGrid(
      startDate,
      endDate,
      siteIds.sort((a, b) => a.localeCompare(b))
    ),
    () => getCogsDataGrid(gridRequest),
    {
      staleTime: staleTimeSettings.HIGH,
      enabled: isEnabled,
    }
  );

  return { gridData: data, gridLoading: isLoading, setGridRequest, gridRequest };
};

/**
 * PERPETUAL INVENTORY
 */

export const useGetPerpetualInventoryKPICardsQuery = (enabled: boolean) => {
  const { getPerpetualInventoryKPICards } = useInventoryRequests();

  const [request, setGetPerpetualInventoryKPICardsRequest] = useState({
    businessDate: '',
    siteId: '',
  } as PerpetualInventoryRequest);

  const { businessDate, siteId } = request;

  const isEnabled = enabled && businessDate.length > 0 && businessDate !== 'Invalid Date' && siteId.length > 0;

  const { data, isLoading } = useQuery(
    useQueryKeys().perpetualInventory.kpiCards(request.businessDate, request.siteId),
    () => getPerpetualInventoryKPICards(request),
    {
      staleTime: staleTimeSettings.HIGH,
      enabled: isEnabled,
    }
  );

  return {
    perpetualInventoryKPICardDataResponse: data,
    setGetPerpetualInventoryKPICardsRequest,
    kpiCardsLoading: isLoading,
    request,
  };
};

export const useGetPerpetualInventoryDataGridQuery = (enabled: boolean) => {
  const { getPerpetualInventoryDataGrid } = useInventoryRequests();

  const [gridRequest, setGridRequest] = useState({
    businessDate: '',
    siteId: '',
  } as PerpetualInventoryRequest);

  const { businessDate, siteId } = gridRequest;

  const isEnabled = enabled && businessDate.length > 0 && businessDate !== 'Invalid Date' && siteId.length > 0;

  const { data, isLoading } = useQuery(
    useQueryKeys().perpetualInventory.dataGrid(businessDate, siteId),
    () => getPerpetualInventoryDataGrid(gridRequest),
    {
      staleTime: staleTimeSettings.HIGH,
      enabled: isEnabled,
    }
  );

  return { gridData: data, gridLoading: isLoading, setGridRequest, gridRequest };
};

/**
 * LOW STOCK REPORT
 */

export const useGetLowStockReportDataQuery = (siteId: string, enabled = true) => {
  const { getLowStockReportData } = useInventoryRequests();

  // Use current date as default
  const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

  const request = {
    date: currentDate,
    siteId,
  };

  const isEnabled = enabled && siteId.length > 0;

  const { data, isLoading, error } = useQuery(
    useQueryKeys().lowStockReport.data(siteId, currentDate),
    () => getLowStockReportData(request),
    {
      staleTime: staleTimeSettings.HIGH,
      enabled: isEnabled,
    }
  );

  return {
    data,
    isLoading,
    error,
    request,
  };
};

/**
 * REAL TIME INVENTORY
 */

export const useGetRealTimeInventoryOnHandQuery = (
  options: ItemsOnHandQueries
): UseQueryResult<RawItemsOnHandResponse> => {
  const { getRealTimeInventoryOnHand } = useInventoryRequests();
  const queryKeys = useQueryKeys();
  const {
    page = 1,
    limit = 50,
    sortBy = 'itemName',
    sortDir = 'asc',
    filterStatus = 'ALL',
    itemName,
    siteId,
  } = options;

  return useQuery(
    queryKeys.realTimeInventory.rawItemsOnHand(page, limit, sortBy, sortDir, filterStatus, siteId, itemName),
    () => getRealTimeInventoryOnHand({ page, limit, sortBy, sortDir, filterStatus, itemName, siteId }),
    {
      keepPreviousData: true,
      staleTime: staleTimeSettings.LOW,
    }
  );
};

/**
 * PURCHASE ORDERS
 */

export const useGetPurchaseOrdersQuery = (
  page: number,
  limit: number,
  filters?: string[],
  sort?: string
): UseQueryResult<Paginated<PurchaseOrdersGridRowResponse>> => {
  const { getPurchaseOrdersGridData } = useInventoryRequests();

  return useQuery(
    useQueryKeys().purchaseOrders.gridData(page, limit, filters, sort),
    () => getPurchaseOrdersGridData({ page, limit, filters, sort }),
    {
      staleTime: staleTimeSettings.HIGH,
      keepPreviousData: true,
    }
  );
};

export const usePurchaseOrderDataQuery = (id: string, enabled: boolean): UseQueryResult<PurchaseOrderDetails> => {
  const { getPurchaseOrder } = useInventoryRequests();

  return useQuery(useQueryKeys().purchaseOrders.byId(id), () => getPurchaseOrder(id), {
    staleTime: staleTimeSettings.LOW,
    refetchOnWindowFocus: false,
    enabled: !!id && enabled,
  });
};

export const usePurchaseOrderAcknowledgementStatus = (
  id: string,
  nepOrganization: string
): UseQueryResult<PurchaseOrderAcknowledgementDetails> => {
  // Make proxy url string
  const path = '/authlessbslproxyncrid';
  const proxyUrl = `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}${path}`;
  const Verb = Req.Firebase.HttpVerb;

  const errorFallback: PurchaseOrderAcknowledgementDetails = {
    id: '',
    poNumber: '',
    status: PurchaseOrderStatus.Cancelled,
    canAcknowledge: false,
  };
  const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/;
  const validRequest = (poId: string) => nepOrganization && poId && uuidRegex.test(poId);

  const axiosPostRequest = () =>
    axios
      .post(proxyUrl, {
        data: {
          proxy: 'purchase-orders',
          verb: Verb.GET,
          pathSegments: ['purchase-orders', id, 'acknowledge'],
          nepOrganization,
        },
      })
      .then((response) => response.data.result as PurchaseOrderAcknowledgementDetails)
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error in axios request:', error);
        return errorFallback;
      });

  return useQuery(
    useQueryKeys().purchaseOrders.acknowledgementStatusById(id),
    () => (validRequest(id) ? axiosPostRequest() : Promise.resolve(errorFallback)),
    {
      staleTime: staleTimeSettings.LOW,
    }
  );
};

export const useGetTop20ItemUsageReportDataQuery = () => {
  const { getTop20ItemUsageReportData } = useInventoryRequests();
  const [request, setGetTop20ItemUsageReportDataRequest] = useState({
    startDate: '',
    endDate: '',
    siteId: '',
    varianceType: 'cost',
  } as Top20ItemUsageReportRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.siteId.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isFetching } = useQuery(
    useQueryKeys().top20ItemUsage.gridData(request.startDate, request.endDate, request.siteId, request.varianceType),
    () => getTop20ItemUsageReportData(request),
    {
      staleTime: staleTimeSettings.HIGH,
      enabled: isEnabled,
      useErrorBoundary: false,
      refetchOnWindowFocus: false,
    }
  );

  return {
    top20ItemUsageReportData: data,
    setGetTop20ItemUsageReportDataRequest,
    isGetTop20ItemUsageReportDataLoading: isFetching,
  };
};
export const useGetAllPurchaseOrderItemsQuery = (orderId: string, enabled = true) => {
  const { getPurchaseOrderItems } = useInventoryRequests();
  const { data, refetch, isLoading, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery(
    useQueryKeys().purchaseOrders.itemsById(orderId),
    ({ pageParam }) => getPurchaseOrderItems(orderId, { page: pageParam?.pagenum ?? 1, limit: 100 }),
    {
      enabled: !!orderId && enabled,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage: Paginated<PurchaseOrderItem>) =>
        lastPage.meta.currentPage >= lastPage.meta.totalPages ? undefined : { pagenum: lastPage.meta.currentPage + 1 },
    }
  );
  return {
    data,
    isLoading,
    isFetching,
    refetch,
    hasNextPage,
    fetchNextPage,
  };
};

export const useGetVendorItemPurchaseOrderDetailsQuery = (
  vendorId: string,
  vendorItemId: string,
  deliveryDate?: string,
  enabled = true
) => {
  const { getPurchaseOrderVendorItem } = useInventoryRequests();
  const formattedDate = deliveryDate ? dayjs(deliveryDate).format('YYYY-MM-DD') : undefined;

  return useQuery(
    useQueryKeys().purchaseOrders.vendorItemDetailsById(vendorId, vendorItemId, formattedDate ?? ''),
    () => getPurchaseOrderVendorItem(vendorId, vendorItemId, formattedDate),
    {
      enabled: !!vendorId && !!vendorItemId && enabled,
      staleTime: staleTimeSettings.MEDIUM,
    }
  );
};

export const useGetPurchaseOrderItemDetailsQuery = (purchaseOrderId: string, purchaseOrderItemId: string) => {
  const { getPurchaseOrderItem } = useInventoryRequests();

  return useQuery(
    useQueryKeys().purchaseOrders.purchaseOrderItemById(purchaseOrderId, purchaseOrderItemId),
    () => getPurchaseOrderItem(purchaseOrderId, purchaseOrderItemId),
    {
      enabled: !!purchaseOrderId && !!purchaseOrderItemId,
      staleTime: staleTimeSettings.MEDIUM,
    }
  );
};

export const useGetInvoiceTemplatesQuery = (vendorIds: string[], templateName: string, page: number, limit: number) => {
  const { getInvoiceTemplates } = useInventoryRequests();

  return useQuery(
    useQueryKeys().vendorTemplates.list(vendorIds, templateName, page, limit),
    () => getInvoiceTemplates(vendorIds, templateName, page, limit),
    {
      staleTime: staleTimeSettings.MEDIUM,
      keepPreviousData: true,
    }
  );
};

export const useGetVendorTemplateFiltersQuery = () => {
  const { getVendorTemplateFilterOptions } = useInventoryRequests();

  return useQuery(useQueryKeys().vendorTemplates.filters(), () => getVendorTemplateFilterOptions(), {
    staleTime: staleTimeSettings.MEDIUM,
  });
};
